import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Archive, ArchiveRestore, MapPin } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TooltipProvider } from '@/components/ui/tooltip';
import { DetailField } from '@/components/common/DetailField';
import {
  DIALOG_TAB_TRIGGER,
  DialogEyebrow,
  SectionHeading,
} from '@/components/common/DialogPrimitives';
import { customersService } from '@/services/customers';
import { ordersService } from '@/services/orders';
import { CustomerAddressesSection } from './CustomerAddressesSection';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { CustomerListRow } from '@/types/customers';

interface Props {
  customer: CustomerListRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (customer: CustomerListRow) => void;
}

export function CustomerDetailSheet({ customer, open, onOpenChange, onEdit }: Props) {
  const qc = useQueryClient();

  // Latest 25 orders for this customer — drives the Orders tab.
  const ordersQuery = useQuery({
    queryKey: ['customers', 'orders', customer?.id],
    queryFn: () => ordersService.list({ pageSize: 25 }),
    enabled: !!customer && open,
  });

  // Addresses — for the default-billing summary on Overview + the Addresses tab.
  const addressesQuery = useQuery({
    queryKey: ['customers', 'addresses', customer?.id],
    queryFn: () => customersService.listAddresses(customer!.id),
    enabled: !!customer && open,
  });
  const billing = useMemo(() => {
    const list = addressesQuery.data ?? [];
    return list.find((a) => a.isDefaultBilling) ?? list[0] ?? null;
  }, [addressesQuery.data]);

  const archive = useMutation({
    mutationFn: () => customersService.archive(customer!.id),
    onSuccess: () => {
      toast.success('Customer archived');
      qc.invalidateQueries({ queryKey: ['customers'] });
      onOpenChange(false);
    },
  });

  const restore = useMutation({
    mutationFn: () => customersService.update(customer!.id, { status: 'ACTIVE' }),
    onSuccess: () => {
      toast.success('Customer restored');
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  const customerOrders = useMemo(() => {
    const all = ordersQuery.data?.rows ?? [];
    if (!customer) return [];
    return all.filter((o) => o.customerId === customer.id);
  }, [ordersQuery.data, customer]);

  return (
    <TooltipProvider delayDuration={150}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[88vh] w-full max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-7 pt-6 pb-5">
            <DialogTitle className="sr-only">{customer?.name ?? 'Customer'}</DialogTitle>
            {customer && (
              <div className="flex items-start gap-4">
                <span
                  aria-hidden="true"
                  className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-[15px] font-semibold text-primary ring-1 ring-inset ring-primary/15"
                >
                  {initials(customer.name)}
                </span>

                <div className="min-w-0 flex-1 space-y-1.5">
                  <DialogEyebrow>{customer.code}</DialogEyebrow>
                  <h2 className="text-[18px] font-semibold leading-tight tracking-tight text-foreground">
                    {customer.name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge
                      variant={customer.status === 'ACTIVE' ? 'success' : 'muted'}
                      className="text-[10px]"
                    >
                      {customer.status === 'ACTIVE' ? 'active' : 'archived'}
                    </Badge>
                    {customer.gstin && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        GSTIN
                      </Badge>
                    )}
                    {customer.externalRef && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        ref {customer.externalRef}
                      </Badge>
                    )}
                    {customer.email && (
                      <span className="text-[11px] text-muted-foreground">
                        · {customer.email}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {customer.status === 'ACTIVE' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => archive.mutate()}
                      disabled={archive.isPending}
                    >
                      <Archive className="h-3.5 w-3.5" />
                      Archive
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => restore.mutate()}
                      disabled={restore.isPending}
                    >
                      <ArchiveRestore className="h-3.5 w-3.5" />
                      Restore
                    </Button>
                  )}
                  {onEdit && (
                    <Button variant="outline" size="sm" onClick={() => onEdit(customer)}>
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogHeader>
          <Separator />

          {/* Stats strip */}
          <div className="flex items-stretch divide-x divide-border/60 border-b border-border/60 bg-muted/20 px-7">
            <Stat label="Lifetime orders" value={fmt.number(customer?._orderCount ?? 0)} accent />
            <Stat label="Returns" value={fmt.number(customer?._returnCount ?? 0)} muted />
            <Stat
              label="Customer since"
              value={customer ? fmt.date(customer.createdAt) : '—'}
              muted
            />
            <Stat
              label="Last update"
              value={customer ? fmt.relative(customer.updatedAt) : '—'}
              muted
            />
          </div>

          <Tabs defaultValue="overview" className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b border-border/60 bg-background px-7">
              <TabsList className="h-10 gap-2 bg-transparent p-0">
                <TabsTrigger value="overview" className={DIALOG_TAB_TRIGGER}>
                  Overview
                </TabsTrigger>
                <TabsTrigger value="addresses" className={DIALOG_TAB_TRIGGER}>
                  <MapPin className="h-3.5 w-3.5" />
                  Addresses
                </TabsTrigger>
                <TabsTrigger value="orders" className={DIALOG_TAB_TRIGGER}>
                  Orders
                </TabsTrigger>
              </TabsList>
            </div>

            <ScrollArea className="flex-1">
              <div className="px-7 py-5">
                <TabsContent value="overview" className="m-0 space-y-5">
                  <section className="space-y-3">
                    <SectionHeading title="Identity" />
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <DetailField label="Code" value={<span className="font-mono">{customer?.code}</span>} />
                      <DetailField label="Name" value={customer?.name} />
                      <DetailField
                        label="External reference"
                        value={customer?.externalRef ?? '—'}
                      />
                    </div>
                  </section>

                  <Separator />
                  <section className="space-y-3">
                    <SectionHeading title="Contact" />
                    <div className="grid grid-cols-2 gap-4">
                      <DetailField label="Email" value={customer?.email ?? '—'} />
                      <DetailField label="Phone" value={customer?.phone ?? '—'} />
                    </div>
                  </section>

                  <Separator />
                  <section className="space-y-3">
                    <SectionHeading title="Tax (India GST)" />
                    <div className="grid grid-cols-2 gap-4">
                      <DetailField
                        label="GSTIN"
                        value={customer?.gstin ? <span className="font-mono">{customer.gstin}</span> : '—'}
                      />
                      <DetailField
                        label="Treatment"
                        value={customer?.gstin ? 'Registered (B2B)' : 'Unregistered'}
                      />
                    </div>
                  </section>

                  <Separator />
                  <section className="space-y-3">
                    <SectionHeading title="Billing address" hint="Flows to the invoice Bill-To" />
                    {billing ? (
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                        <DetailField label="Street" value={billing.line1} />
                        <DetailField label="Area" value={billing.line2 ?? '—'} />
                        <DetailField label="City" value={billing.city} />
                        <DetailField label="State" value={billing.state} />
                        <DetailField label="PIN" value={billing.postalCode ?? '—'} />
                        <DetailField
                          label="GSTIN"
                          value={billing.gstin ? <span className="font-mono">{billing.gstin}</span> : '—'}
                        />
                      </div>
                    ) : (
                      <p className="text-[13px] text-muted-foreground">
                        No billing address yet — add one via Edit, and it’ll appear on the customer’s invoices.
                      </p>
                    )}
                  </section>
                </TabsContent>

                <TabsContent value="addresses" className="m-0 space-y-4">
                  <SectionHeading
                    title="Address book"
                    hint="Defaults drive invoice + dispatch fields"
                  />
                  <CustomerAddressesSection customerId={customer?.id ?? null} />
                </TabsContent>

                <TabsContent value="orders" className="m-0 space-y-4">
                  <SectionHeading
                    title="Recent orders"
                    hint={
                      ordersQuery.isLoading
                        ? 'Loading…'
                        : `${customerOrders.length} found`
                    }
                  />
                  {customerOrders.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border/70 px-4 py-10 text-center text-[12.5px] text-muted-foreground">
                      No orders booked for this customer yet.
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-border/70">
                      {customerOrders.map((o) => (
                        <div
                          key={o.id}
                          className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0"
                        >
                          <div className="col-span-3 font-mono">{o.orderNumber}</div>
                          <div className="col-span-3 text-muted-foreground">
                            {fmt.date(o.createdAt)}
                          </div>
                          <div className="col-span-3 text-muted-foreground">
                            {o.status.toLowerCase().replace(/_/g, ' ')}
                          </div>
                          <div className="col-span-3 text-right font-mono tabular-nums">
                            {o.grandTotal != null
                              ? fmt.inr(Number(o.grandTotal))
                              : o.totalAmount != null
                                ? fmt.inr(Number(o.totalAmount))
                                : '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

function Stat({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex-1 min-w-[110px] px-4 py-3.5 first:pl-0 last:pr-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-1.5 font-mono text-[19px] leading-none tabular-nums',
          accent ? 'font-semibold text-foreground' : muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
